// SurveyDialogWithBlossoms.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormGroup,
  Box,
  Paper,
} from "@mui/material";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";

const SurveyDialog = ({ open, onClose, surveyId }) => {
  const [answers, setAnswers] = useState({
    participantCount: "",
    zoom: "",
    metInPerson: "",
    sharedTheme: "",
    ddpReviewed: "",
    talkedAboutReview: "",
    futurePlans: [],
    marcheParticipation: "",
    marcheRole: "",
    marcheImpression: "",
    nextMarchePlan: "",
  });

  const [isThankYouDialogOpen, setThankYouDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const blossoms = [];

    const intervalId = setInterval(() => {
      const i = Math.floor(Math.random() * 100000);
      const blossom = document.createElement("div");
      blossom.textContent = "🌸";
      blossom.style.position = "fixed";
      blossom.style.top = "-30px";
      blossom.style.left = Math.random() * 100 + "vw";
      blossom.style.fontSize = "24px";
      blossom.style.opacity = 0.7;
      blossom.style.pointerEvents = "none";
      blossom.style.zIndex = 9999;
      blossom.style.animation = `fall-${i} ${
        5 + Math.random() * 5
      }s linear forwards`;

      const keyframes = `
        @keyframes fall-${i} {
          0% { transform: translateY(-30px) rotate(0deg); opacity: 0.9; }
          100% { transform: translateY(100vh) rotate(${
            Math.random() * 360
          }deg); opacity: 0.1; }
        }
      `;
      const styleSheet = document.createElement("style");
      styleSheet.type = "text/css";
      styleSheet.innerText = keyframes;
      document.head.appendChild(styleSheet);

      document.body.appendChild(blossom);
      blossoms.push({ blossom, styleSheet });

      // アニメーションが終わったら削除
      setTimeout(() => {
        blossom.remove();
        styleSheet.remove();
      }, 10000);
    }, 800);

    return () => {
      clearInterval(intervalId);
    };
  }, [open]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "futurePlans") {
      setAnswers((prev) => ({
        ...prev,
        futurePlans: checked
          ? [...prev.futurePlans, value]
          : prev.futurePlans.filter((item) => item !== value),
      }));
    } else if (name === "marcheParticipation") {
      setAnswers((prev) => ({
        ...prev,
        marcheParticipation: value,
        ...(value === "いいえ" && { marcheRole: "", marcheImpression: "" }),
      }));
    } else {
      setAnswers((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    const user = auth.currentUser;
    if (!user) return alert("認証エラー");

    const surveyData = {
      createdAt: serverTimestamp(),
      surveyId,
      answers: { ...answers },
      userId: user.uid,
    };

    try {
      await setDoc(doc(db, "users", user.uid, "fab", surveyId), surveyData);
      await addDoc(collection(db, "fab"), surveyData);
      setThankYouDialogOpen(true);
    } catch (err) {
      console.error("保存失敗", err);
      alert("保存に失敗しました");
    }
  };

  const closeThankYou = () => {
    setThankYouDialogOpen(false);
    onClose();
  };

  const renderQuestion = (emoji, question, name, children) => (
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 3, borderRadius: 3, background: "#f0f7f4" }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {emoji} {question}
      </Typography>
      {children}
    </Paper>
  );

  return (
    <>
      <Dialog
        open={open && !isThankYouDialogOpen}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            color: "#2e7d32",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          🌱 アンケートご協力のお願い
        </DialogTitle>
        <DialogContent sx={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
          {renderQuestion(
            "👥",
            "参加人数",
            "participantCount",
            <Select
              name="participantCount"
              value={answers.participantCount}
              onChange={handleChange}
              fullWidth
            >
              {Array.from({ length: 15 }, (_, i) => (
                <MenuItem key={i + 1} value={`${i + 1}人`}>
                  {i + 1}人
                </MenuItem>
              ))}
            </Select>
          )}

          {renderQuestion(
            "📹",
            "グループ内で、Zoom等のオンラインツールを使ってコミュニケーションを取りましたか？",
            "zoom",
            <RadioGroup
              name="zoom"
              value={answers.zoom}
              onChange={handleChange}
            >
              <FormControlLabel value="はい" control={<Radio />} label="はい" />
              <FormControlLabel
                value="いいえ"
                control={<Radio />}
                label="いいえ"
              />
            </RadioGroup>
          )}

          {renderQuestion(
            "🤝",
            "実際に会いましたか？もしくは、今後会う予定はありますか？",
            "metInPerson",
            <RadioGroup
              name="metInPerson"
              value={answers.metInPerson}
              onChange={handleChange}
            >
              <FormControlLabel value="はい" control={<Radio />} label="はい" />
              <FormControlLabel
                value="いいえ"
                control={<Radio />}
                label="いいえ"
              />
            </RadioGroup>
          )}

          {renderQuestion(
            "🧠",
            "お互いをより深く知るためのテーマについて、話し合いましたか？",
            "sharedTheme",
            <RadioGroup
              name="sharedTheme"
              value={answers.sharedTheme}
              onChange={handleChange}
            >
              <FormControlLabel value="はい" control={<Radio />} label="はい" />
              <FormControlLabel
                value="いいえ"
                control={<Radio />}
                label="いいえ"
              />
              <FormControlLabel
                value="わからない"
                control={<Radio />}
                label="わからない"
              />
            </RadioGroup>
          )}

          {renderQuestion(
            "📝",
            "グループメンバー同士で、DDPの添削を行いまいしたか？",
            "ddpReviewed",
            <RadioGroup
              name="ddpReviewed"
              value={answers.ddpReviewed}
              onChange={handleChange}
            >
              <FormControlLabel value="はい" control={<Radio />} label="はい" />
              <FormControlLabel
                value="いいえ"
                control={<Radio />}
                label="いいえ"
              />
            </RadioGroup>
          )}

          {renderQuestion(
            "💬",
            "ゆめナビに掲載されているDDPの添削について、グループ内で話し合いましたか？",
            "talkedAboutReview",
            <RadioGroup
              name="talkedAboutReview"
              value={answers.talkedAboutReview}
              onChange={handleChange}
            >
              <FormControlLabel value="はい" control={<Radio />} label="はい" />
              <FormControlLabel
                value="いいえ"
                control={<Radio />}
                label="いいえ"
              />
            </RadioGroup>
          )}

          {renderQuestion(
            "📌",
            "ゆめナビの添削活動について、今後のご予定として当てはまるものを以下よりお選びください（複数選択可）。",
            "futurePlans",
            <FormGroup>
              {[
                "メールで添削結果を送る",
                "Zoomで添削会を行う",
                "リアルで添削会を行う",
              ].map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      name="futurePlans"
                      value={item}
                      checked={answers.futurePlans.includes(item)}
                      onChange={handleChange}
                    />
                  }
                  label={item}
                />
              ))}
            </FormGroup>
          )}

          {renderQuestion(
            "🛍️",
            "3月30日に開催されたマルシェに参加しましたか？",
            "marcheParticipation",
            <>
              <RadioGroup
                name="marcheParticipation"
                value={answers.marcheParticipation}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="はい"
                  control={<Radio />}
                  label="はい"
                />
                <FormControlLabel
                  value="いいえ"
                  control={<Radio />}
                  label="いいえ"
                />
              </RadioGroup>
              {answers.marcheParticipation === "はい" && (
                <>
                  <TextField
                    name="marcheRole"
                    label="どのような役割をしましたか？"
                    value={answers.marcheRole}
                    onChange={handleChange}
                    fullWidth
                    margin="dense"
                  />
                  <TextField
                    name="marcheImpression"
                    label="感想"
                    value={answers.marcheImpression}
                    onChange={handleChange}
                    fullWidth
                    margin="dense"
                    multiline
                  />
                </>
              )}
            </>
          )}

          {renderQuestion(
            "🎯",
            "7月5日の活動には、どのような形で関わりたいですか？",
            "nextMarchePlan",
            <TextField
              label="自由記述"
              name="nextMarchePlan"
              value={answers.nextMarchePlan}
              onChange={handleChange}
              fullWidth
              multiline
              margin="normal"
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button onClick={handleSave} variant="contained" color="success">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isThankYouDialogOpen}
        onClose={closeThankYou}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>🌸 ご協力ありがとうございました</DialogTitle>
        <DialogContent>
          <Typography>
            🙏 アンケートのご記入、ありがとうございました。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeThankYou} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SurveyDialog;
